import { NgIf } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DEFAULT_TENANT_LICENSE_AGENT_TO_CHECK, isDefined } from '@shared/utils';
import { filter } from 'rxjs';
import { environment } from '../environments/environment';
import { CacheService } from './core/cache/cache.service';
import { LocalStorageKey } from './core/cache/local-storage-key.enum';
import { getBrowserLanguage } from './core/i18n/transloco.utils';
import { UiKitAlertComponent } from './core/ui-kit/components/ui-kit-alert/ui-kit-alert.component';
import { UiKitBaseComponent } from './core/ui-kit/components/ui-kit-base/ui-kit-base.component';
import { AlertService } from './core/ui-kit/services/alert.service';
import { AuthenticationService } from './features/authentication/services/authentication.service';
import { LicenseService } from './features/license/data-access-license/service/license.service';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { ViewportWarningComponent } from './shared/components/viewport-warning/viewport-warning.component';
import { UIFacade } from './shared/data/data-access-ui';
import { DebugService } from './shared/services/debug.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [NgIf, RouterOutlet, UiKitAlertComponent, TranslocoPipe, LoaderComponent, ViewportWarningComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent extends UiKitBaseComponent implements OnInit {
  private readonly alertService = inject(AlertService);
  private readonly translocoService = inject(TranslocoService);
  private readonly authService = inject(AuthenticationService);
  private readonly cacheService = inject(CacheService);
  private readonly debugService = inject(DebugService);
  private readonly licenseService = inject(LicenseService);
  private readonly uiFacade = inject(UIFacade);

  private readonly destroyRef = inject(DestroyRef);

  title = 'klens-frontend';
  showAlert = toSignal(this.alertService.showAlert$);

  ngOnInit(): void {
    this.setDebugMode();

    const isDebugModeActive = this.debugService.isDebugModeActive();

    console.log(
      `%cApp Version: %c${environment.version}`,
      'color: white; background: #444; padding: 4px 8px; border-radius: 4px 0 0 4px;',
      'color: #fff; background: #007acc; padding: 4px 8px; border-radius: 0 4px 4px 0; font-weight: bold;',
    );
    console.log(
      `%cDebug Mode: %c${isDebugModeActive}`,
      'color: white; background: #444; padding: 4px 8px; border-radius: 4px 0 0 4px;',
      'color: #fff; background: #007acc; padding: 4px 8px; border-radius: 0 4px 4px 0; font-weight: bold;',
    );

    if (!isDebugModeActive) {
      console.log(
        '%cEnable debug mode by setting %cdbm: true%c in local storage.',
        'color: white; background: #007acc; padding: 4px; border-radius: 4px;',
        'color: yellow; background: #333; padding: 4px; border-radius: 4px;',
        'color: white; background: #007acc; padding: 4px; border-radius: 4px;',
      );
    }

    this.setDefaultLanguage();

    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef), filter(Boolean)).subscribe((user) => {
      this.translocoService.setActiveLang(user.language);
      this.setChatVisibility();
    });
  }

  private setChatVisibility(): void {
    this.licenseService
      .isChatActiveForTenantAgent(DEFAULT_TENANT_LICENSE_AGENT_TO_CHECK)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isChatVisible) => this.uiFacade.setChatVisible(isChatVisible));
  }

  /**
   * If the user is not logged then set the language to the browser's one,
   * if it's not valid then use the fallback it-IT (Already defined by the transloco provider)
   */
  private setDefaultLanguage(): void {
    this.translocoService.setActiveLang(getBrowserLanguage());
  }

  private setDebugMode(): void {
    const isAutomaticDebugActive = environment.automaticDebug;

    if (!isAutomaticDebugActive) {
      return;
    }

    const debugMode = this.cacheService.get<boolean>(LocalStorageKey.DEBUG_MODE);

    if (!isDefined(debugMode)) {
      this.cacheService.set(LocalStorageKey.DEBUG_MODE, true);
    }
  }
}
